import React, { useState, useContext } from "react";
import CountryContext from "CountryContext";

import {
  createFilterParams,
  createOrderingParams,
} from "../../../components/Tables/utils";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { tableIcons } from "../../../ui-components/TableIcons";
import TableStyle from "../../../components/TableStyle";
import Badge from "../../../ui-components/Badge";
import MaterialTable from "@material-table/core";
import _ from "lodash";

import { OPTIONS, LOCALIZATION } from "../Worksites/DetailTable";
import { getFilteredProducts, lkpField } from "./Products.lib";
import WorksiteNameColumn from "components/WorksiteNameColumn";
      

const getColumns = (intl, worksiteTypes, lines, classes, countryCode) => [
  {
    title: "Link",
    field: "worksite_id",
    filtering: false,
    defaultSort: "desc",
    render: ({ worksite_id }) => (
      <div>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`/${countryCode}/worksites/${worksite_id}`}
        >
          <ArrowTopRightOnSquareIcon
            className={`w-6 h-6 mt-2 text-kerakoll-400 cursor-pointer`}
          />
        </a>
      </div>
    ),
  },
  /*   {
    title: intl.formatMessage({id: "name"}),
    field: "des_worksite",
    type: "string",
  }, */
  {
    title: intl.formatMessage({ id: "name" }),
    field: "address",
    type: "string",
    headerStyle: { minWidth: 250 },
    cellStyle: { minWidth: 250, textTransform: "uppercase" },
    render: ({ address, des_worksite, city }) => (
      <div className="font-semibold">
        <WorksiteNameColumn des_worksite={des_worksite} address={address} city={city} />
      </div>
    ),
  },
  {
    title: intl.formatMessage({ id: "city" }),
    field: "city",
    type: "string",
    headerStyle: { minWidth: 220 },
    cellStyle: { minWidth: 220, textTransform: "uppercase" },
  },
  {
    title: "Prov",
    field: "cod_province",
    type: "string",
  },
  {
    title: "Tipo",
    field: "cod_worksite_type",
    lookup: worksiteTypes.reduce(
      (acc, i) => ({ ...acc, [i.cod_worksite_type]: i.des_worksite_type }),
      {}
    ),
    render: ({ cod_worksite_type }) => (
      <div>{intl.formatMessage({ id: cod_worksite_type })}</div>
    ),
  },
  {
    title: "Linee",
    field: "lines",
    lookup: lines.reduce((acc, i) => ({ ...acc, [i]: i }), {}),
    render: function (row) {
      return row["lines"] ? (
        <div>
          {row["lines"].map((s) =>
            s ? (
              <Badge
                classNames="mb-1 mr-1"
                text={s}
                key={s}
                textSize="text-xxs"
              />
            ) : null
          )}
        </div>
      ) : null;
    },
  },
  {
    title: "Classi di budget",
    field: "classes",
    cellStyle: { minWidth: 250 },
    headerStyle: { minWidth: 250, whiteSpace: "nowrap" },
    lookup: classes.reduce((acc, i) => ({ ...acc, [i]: i }), {}),
    render: function (row) {
      return row["classes"] ? (
        <div>
          {row["classes"].map((s) =>
            s ? (
              <Badge
                classNames="mb-1 mr-1"
                text={s}
                key={s}
                textSize="text-xxs"
              />
            ) : null
          )}
        </div>
      ) : null;
    },
  },
  {
    title: "Valore",
    field: "amt_total",
    type: "string",
    filtering: false,
    render: ({ amt_total }) => (
      <div className="font-semibold text-lg">
        {intl.formatNumber(amt_total, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        })}
      </div>
    ),
  },
];

function ProductsDetailTable({
  intl,
  push,
  countryApiRequest,
  analysisPeriod,
  selectedStatus,
  visibilityUserId,
  worksiteTypes,
  products,
  mainFilters,
}) {
  const {
    country: { code },
  } = useContext(CountryContext);

  const countryCode = code;

  const ref = React.useRef(null);
  const [columns, setColumns] = useState([]);

  const lines = Array.from(
    new Set(products.map((i) => i[lkpField["lines"]]))
  ).sort();
  const filteredProducts = getFilteredProducts(
    "classes",
    products,
    mainFilters
  );
  const classes = Array.from(
    new Set(filteredProducts.map((i) => i[lkpField["classes"]]))
  ).sort();

  return (
    <>
      <TableStyle>
        <MaterialTable
          tableRef={ref}
          columns={columns}
          data={(query) =>
            new Promise((resolve, reject) => {
              const filters = createFilterParams(query);
              const ordering = createOrderingParams(query);
              const payload = {
                start_date: analysisPeriod.start,
                end_date: analysisPeriod.end,
                visibility_user_id: visibilityUserId,
                filter_status: selectedStatus.length
                  ? selectedStatus.map((i) => i.value)
                  : undefined,
                lines: mainFilters.lines.length
                  ? mainFilters.lines.map((i) => i.value)
                  : undefined,
                classes: mainFilters.classes.length
                  ? mainFilters.classes.map((i) => i.value)
                  : undefined,
                products: mainFilters.products.length
                  ? mainFilters.products.map((i) => i.value)
                  : undefined,
                ...{ offset: query.page * query.pageSize },
                ...{ limit: query.pageSize },
                ...(filters && { filters }),
                ...(ordering && { ordering }),
              };
              return countryApiRequest
                .post("/analytics/products/detail", payload)
                .then((result) => {
                  if (!columns.length) {
                    setColumns(getColumns(intl, worksiteTypes, lines, classes, countryCode));
                  }
                  resolve({
                    data: result["items"],
                    page: query.page,
                    totalCount: result["total"],
                  });
                })
                .catch((err) => {
                  console.log(err);
                  push({
                    title: "Errore durante il caricamento",
                    type: "error",
                  });
                  reject();
                });
            })
          }
          icons={tableIcons}
          options={OPTIONS}
          localization={LOCALIZATION}
        />
      </TableStyle>
    </>
  );
}

export default React.memo(ProductsDetailTable, (props, nextProps) => {
  // Render component only when users changes
  return (
    _.isEqual(props.analysisPeriod, nextProps.analysisPeriod) &&
    _.isEqual(props.selectedStatus, nextProps.selectedStatus) &&
    _.isEqual(props.products, nextProps.products) &&
    _.isEqual(props.mainFilters.lines, nextProps.mainFilters.lines)
  );
});
